/**
 * Performance optimization utilities
 * Reduces main thread work and optimizes rendering
 */

// Scheduler for deferring non-critical work
export const scheduleIdleWork = (callback: () => void, timeout = 2000) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 1);
  }
};

// Batch DOM updates to reduce reflows
export const batchDOMUpdates = (updates: (() => void)[]) => {
  if (updates.length === 0) return;
  
  // Use requestAnimationFrame to batch updates
  requestAnimationFrame(() => {
    updates.forEach(update => update());
  });
};

// Debounce function for performance-critical operations
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function for scroll/resize handlers
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Optimize long tasks by breaking them into smaller chunks
export const chunkTask = (
  task: () => void,
  chunkSize = 50,
  delay = 0
): Promise<void> => {
  return new Promise((resolve) => {
    const startTime = performance.now();
    
    const processChunk = () => {
      const currentTime = performance.now();
      
      if (currentTime - startTime < chunkSize) {
        task();
        setTimeout(processChunk, delay);
      } else {
        resolve();
      }
    };
    
    scheduleIdleWork(processChunk);
  });
};

// Preload critical resources
export const preloadResource = (
  href: string,
  as: string,
  crossorigin?: string
) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as as any;
  if (crossorigin) {
    link.crossOrigin = crossorigin;
  }
  document.head.appendChild(link);
};

// Optimize images with intersection observer
export const optimizeImages = () => {
  if (!('IntersectionObserver' in window)) return;
  
  const imageObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    },
    { rootMargin: '50px' }
  );
  
  document.querySelectorAll('img[data-src]').forEach((img) => {
    imageObserver.observe(img);
  });
};

// Reduce animation complexity on low-end devices
export const optimizeAnimations = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4;
  
  if (prefersReducedMotion || isLowEndDevice) {
    document.documentElement.style.setProperty('--animation-duration', '0.01s');
    document.documentElement.classList.add('reduced-motion');
  }
};

// Initialize performance optimizations
export const initPerformanceOptimizations = () => {
  // Optimize images
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', optimizeImages);
  } else {
    optimizeImages();
  }
  
  // Optimize animations
  optimizeAnimations();
  
  // Note: Don't preload source files (.tsx) - they are compiled and bundled by Vite
  // Preloading source files causes warnings because they don't exist at runtime
  // Vite handles code splitting and lazy loading automatically
};

